import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ChevronLeft, ChevronRight, CheckCircle, Lock, BookOpen, MessageSquare, Send, X, Loader, Award, RotateCcw } from '../components/CustomIcons';
import { toast } from 'sonner';

function CreditFloat({ credits, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 1600); return () => clearTimeout(t); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div style={{ position: 'fixed', top: '30%', left: '50%', transform: 'translateX(-50%)', zIndex: 1000, pointerEvents: 'none' }}
      className="animate-float-credit">
      <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '32px', color: '#F97316', textShadow: '0 0 20px rgba(249,115,22,0.8)' }}>+{credits} Credits!</div>
    </div>
  );
}

export default function CoursePlayer() {
  const { pathwayId, courseId, lessonId } = useParams();
  const { authFetch, token } = useAuth();
  const [pathway, setPathway] = useState(null);
  const [currentCourse, setCurrentCourse] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tutorOpen, setTutorOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMsg, setInputMsg] = useState('');
  const [tutorLoading, setTutorLoading] = useState(false);
  const [floatCredit, setFloatCredit] = useState(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [completing, setCompleting] = useState(false);
  const chatEndRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    authFetch(`/courses/pathways/${pathwayId}`)
      .then(r => {
        setPathway(r.data);
        const courses = r.data.courses || [];
        const targetCourse = courseId ? courses.find(c => c.id === courseId) : courses[0];
        const tc = targetCourse || courses[0];
        setCurrentCourse(tc);
        const lessons = tc?.lessons || [];
        const targetLesson = lessonId ? lessons.find(l => l.id === lessonId) : lessons[0];
        setCurrentLesson(targetLesson || lessons[0]);
      })
      .catch(e => console.error('CoursePlayer load error:', e))
      .finally(() => setLoading(false));
  }, [pathwayId, courseId, lessonId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const markComplete = async () => {
    if (!currentLesson || completing) return;
    setCompleting(true);
    try {
      const res = await authFetch(`/courses/lessons/${currentLesson.id}/complete`, { method: 'post' });
      if (!res.data.already_done) {
        const earned = res.data.credits_earned || 10;
        setFloatCredit(earned);
        toast.success(`Lesson complete! +${earned} credits earned.`, { duration: 3500 });
      }
      // Navigate to next lesson
      const allLessons = pathway?.courses?.flatMap(c => c.lessons) || [];
      const idx = allLessons.findIndex(l => l.id === currentLesson.id);
      if (idx < allLessons.length - 1) {
        const next = allLessons[idx + 1];
        const nextCourse = pathway.courses.find(c => c.lessons.some(l => l.id === next.id));
        authFetch('/courses/save-position', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pathway_id: pathwayId, course_id: nextCourse?.id, lesson_id: next.id })
        }).catch(() => {});
        navigate(`/learn/${pathwayId}/${nextCourse?.id}/${next.id}`);
      } else {
        setShowQuiz(true);
      }
    } catch (e) { console.error('complete-lesson error:', e); }
    setCompleting(false);
  };

  const sendTutorMessage = async () => {
    if (!inputMsg.trim() || tutorLoading) return;
    const msg = inputMsg.trim();
    setInputMsg('');
    setMessages(prev => [...prev, { role: 'user', content: msg, id: `u-${Date.now()}` }]);
    setTutorLoading(true);

    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API}/api/ai-tutor/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          message: msg,
          session_id: `${pathwayId}-${courseId || 'main'}`,
          context: [
            pathway?.name ? `Pathway: ${pathway.name}` : '',
            currentCourse?.title ? `Course: ${currentCourse.title}` : '',
            currentLesson?.title ? `Lesson: ${currentLesson.title}` : '',
          ].filter(Boolean).join(' | ')
        })
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let aiMsg = '';
      setMessages(prev => [...prev, { role: 'assistant', content: '', id: `a-${Date.now()}` }]);
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const text = decoder.decode(value);
        const lines = text.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') break;
            aiMsg += data;
            setMessages(prev => { const msgs = [...prev]; msgs[msgs.length - 1] = { role: 'assistant', content: aiMsg }; return msgs; });
          }
        }
      }
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: "I'm having trouble connecting. Please try again!" }]);
    }
    setTutorLoading(false);
  };

  const submitQuiz = async () => {
    if (!currentCourse) return;
    const res = await authFetch(`/courses/quiz/${currentCourse.id}/submit`, { method: 'post', data: { answers: quizAnswers } });
    setQuizResult(res.data);
    if (res.data.passed) {
      if (res.data.credits_earned > 0) setFloatCredit(res.data.credits_earned);
      const pct = Math.round((res.data.score || 0) * 100);
      toast.success(`Quiz passed with ${pct}%! +${res.data.credits_earned || 25} credits earned.`, { duration: 4000 });
    }
  };

  const SUGGESTED_QUESTIONS = ['Explain this in simpler terms', 'Give me a real-world example', 'Quiz me on this topic', 'What do I need to know for the exam?'];

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}><div className="animate-spin" style={{ width: '32px', height: '32px', border: '2px solid var(--border)', borderTopColor: 'var(--orange)', borderRadius: '50%', margin: '0 auto 12px' }} />Loading course...</div>;

  if (!pathway || !currentLesson) return (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <p style={{ color: 'var(--text-muted)' }}>Course not found. <Link to="/learn" style={{ color: '#F97316' }}>Back to courses</Link></p>
    </div>
  );

  const allLessons = pathway.courses?.flatMap(c => c.lessons.map(l => ({ ...l, courseId: c.id, courseName: c.title }))) || [];
  const currentIdx = allLessons.findIndex(l => l.id === currentLesson.id);
  const prevLesson = currentIdx > 0 ? allLessons[currentIdx - 1] : null;
  const nextLesson = currentIdx < allLessons.length - 1 ? allLessons[currentIdx + 1] : null;

  return (
    <div style={{ background: 'var(--bg-page)', minHeight: '100vh', display: 'flex', flexDirection: 'column' }} data-testid="course-player">
      {floatCredit && <CreditFloat credits={floatCredit} onDone={() => setFloatCredit(null)} />}

      {/* Breadcrumb */}
      <div style={{ background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-subtle)', padding: '12px 24px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Link to="/learn" style={{ color: 'var(--text-secondary)', fontSize: '13px', textDecoration: 'none' }}>Pathways</Link>
          <ChevronRight size={12} color="#444444" />
          <Link to={`/learn/${pathwayId}`} style={{ color: 'var(--text-secondary)', fontSize: '13px', textDecoration: 'none' }}>{pathway.name}</Link>
          <ChevronRight size={12} color="#444444" />
          <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{currentLesson.title}</span>
        </div>
        <a
          href={`/ai-tutor?pathway=${encodeURIComponent(pathway?.name || '')}&course=${encodeURIComponent(currentCourse?.title || '')}&lesson=${encodeURIComponent(currentLesson?.title || '')}`}
          style={{ fontSize: '12px', color: 'var(--orange)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, flexShrink: 0 }}
          data-testid="open-fullpage-tutor-link">
          <MessageSquare size={13} />
          Full AI Counselor
        </a>
      </div>

      <div style={{ display: 'flex', flex: 1 }}>
        {/* Main content */}
        <div style={{ flex: 1, padding: '32px 40px', maxWidth: tutorOpen ? 'calc(100% - 340px)' : '100%', transition: 'max-width 0.3s ease', overflowY: 'auto' }}>
          <div style={{ maxWidth: '720px' }}>
            <div style={{ marginBottom: '24px' }}>
              <span className="badge-grey" style={{ marginBottom: '8px', display: 'inline-block' }}>{currentCourse?.title}</span>
              <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '28px', color: 'var(--text-primary)', margin: '8px 0 4px 0' }}>{currentLesson.title}</h1>
              <p style={{ color: 'var(--text-secondary)', fontSize: '13px', fontFamily: 'Inter, sans-serif', margin: 0 }}>~{currentLesson.duration_min || 25} min · Reading</p>
            </div>

            {/* Quiz Modal */}
            {showQuiz && currentCourse?.quiz && (
              <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
                <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '20px', color: 'var(--text-primary)', marginBottom: '20px' }}>Module Quiz: {currentCourse.title}</h2>
                {!quizResult ? (
                  <>
                    {currentCourse.quiz.questions?.map((q, qi) => (
                      <fieldset key={q.id} style={{ border: 'none', padding: 0, margin: '0 0 20px 0', paddingBottom: '20px', borderBottom: '1px solid var(--border-default)' }}>
                        <legend style={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '12px', fontFamily: 'Inter, sans-serif', fontSize: '14px', display: 'block', width: '100%' }}>Q{qi + 1}. {q.question}</legend>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {q.options.map(opt => (
                            <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '10px 14px', borderRadius: '8px', border: quizAnswers[q.id] === opt ? '1px solid var(--orange)' : '1px solid var(--border)', background: quizAnswers[q.id] === opt ? 'var(--orange-subtle)' : 'transparent', transition: 'all 0.2s' }}>
                              <input type="radio" name={q.id} value={opt} checked={quizAnswers[q.id] === opt} onChange={() => setQuizAnswers(prev => ({ ...prev, [q.id]: opt }))} style={{ accentColor: '#F97316' }} aria-label={opt} />
                              <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>{opt}</span>
                            </label>
                          ))}
                        </div>
                      </fieldset>
                    ))}
                    <button className="btn-primary" onClick={submitQuiz} disabled={Object.keys(quizAnswers).length < (currentCourse.quiz.questions?.length || 0)} style={{ fontSize: '14px' }}
                      aria-label={`Submit quiz (${Object.keys(quizAnswers).length} of ${currentCourse.quiz.questions?.length || 0} answered)`}>Submit Quiz</button>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>{quizResult.passed ? '🎉' : '📚'}</div>
                    <h3 style={{ color: quizResult.passed ? '#22C55E' : '#F97316', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '28px' }}>
                      {quizResult.score}% — {quizResult.passed ? 'Passed!' : 'Keep Studying'}
                    </h3>
                    <p style={{ color: 'var(--text-muted)', marginBottom: '16px' }}>{quizResult.correct}/{quizResult.total} correct</p>
                    {quizResult.passed ? (
                      <><p style={{ color: '#22C55E', fontWeight: 600 }}>+{quizResult.credits_earned} Credits Earned!</p>
                      <Link to={`/learn/${pathwayId}`} className="btn-primary" style={{ textDecoration: 'none', display: 'inline-block', marginTop: '12px' }}>Continue Pathway →</Link></>
                    ) : (
                      <button className="btn-secondary" onClick={() => { setQuizResult(null); setQuizAnswers({}); }}><RotateCcw size={14} />Retake Quiz</button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Lesson Content */}
            <div style={{ color: 'var(--text-secondary)', lineHeight: 1.8, fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>
              {currentLesson.content?.split('\n').map((para, i) => para.trim() && (
                <p key={i} style={{ marginBottom: '16px' }}>{para}</p>
              ))}
              {/* Key concept callout */}
              <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderLeft: '3px solid #F97316', borderRadius: '8px', padding: '16px 18px', margin: '20px 0' }}>
                <p style={{ color: '#F97316', fontWeight: 600, fontFamily: 'Plus Jakarta Sans, sans-serif', marginBottom: '6px', fontSize: '13px' }}>KEY CONCEPT</p>
                <p style={{ color: 'var(--text-primary)', margin: 0 }}>This lesson introduces foundational knowledge that you'll build on throughout the {pathway.name} pathway. Take notes and use the AI Counselor if anything is unclear.</p>
              </div>
            </div>

            {/* Bottom navigation */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '32px', paddingTop: '24px', borderTop: '1px solid var(--border)' }}>
              <div>
                {prevLesson ? (
                  <Link to={`/learn/${pathwayId}/${prevLesson.courseId}/${prevLesson.id}`} className="btn-secondary" style={{ textDecoration: 'none', fontSize: '14px' }}>
                    <ChevronLeft size={14} /> Previous
                  </Link>
                ) : <div />}
              </div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Lesson {currentIdx + 1} of {allLessons.length}</span>
                <button onClick={markComplete} disabled={completing} className="btn-primary" style={{ fontSize: '14px' }} data-testid="mark-complete-btn">
                  {completing ? 'Saving...' : currentLesson.completed ? '✓ Completed' : 'Mark Complete'}
                </button>
              </div>
              <div>
                {nextLesson ? (
                  <Link to={`/learn/${pathwayId}/${nextLesson.courseId}/${nextLesson.id}`} className="btn-secondary" style={{ textDecoration: 'none', fontSize: '14px' }}>
                    Next <ChevronRight size={14} />
                  </Link>
                ) : <div />}
              </div>
            </div>
          </div>
        </div>

        {/* Course outline sidebar */}
        {!tutorOpen && (
          <div style={{ width: '280px', background: 'var(--bg-surface)', borderLeft: '1px solid var(--border)', overflowY: 'auto', padding: '20px 0', flexShrink: 0 }} className="hidden lg:block">
            <div style={{ padding: '0 16px 16px' }}>
              <h4 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)', margin: '0 0 12px 0' }}>Course Outline</h4>
            </div>
            {pathway.courses?.map(course => (
              <div key={course.id} style={{ marginBottom: '4px' }}>
                <div style={{ padding: '8px 16px', fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  M{course.module_number}: {course.title}
                </div>
                {course.lessons?.map(lesson => (
                  <Link key={lesson.id} to={`/learn/${pathwayId}/${course.id}/${lesson.id}`} style={{ textDecoration: 'none', display: 'block' }}>
                    <div style={{ padding: '8px 16px 8px 24px', display: 'flex', alignItems: 'center', gap: '8px', background: lesson.id === currentLesson?.id ? 'rgba(249,115,22,0.1)' : 'transparent', borderLeft: lesson.id === currentLesson?.id ? '2px solid #F97316' : '2px solid transparent' }}>
                      {lesson.completed ? <CheckCircle size={13} color="#22C55E" /> : <div style={{ width: '13px', height: '13px', borderRadius: '50%', border: '1px solid #444444' }} />}
                      <span style={{ fontSize: '13px', color: lesson.id === currentLesson?.id ? '#F97316' : '#999999', lineHeight: 1.3 }}>{lesson.title}</span>
                    </div>
                  </Link>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* AI Tutor Panel */}
        <div style={{ position: 'fixed', right: tutorOpen ? 0 : '-340px', top: 0, width: '340px', height: '100vh', background: 'var(--bg-surface)', borderLeft: '1px solid var(--border)', transition: 'right 0.3s ease', zIndex: 40, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h4 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)', margin: '0 0 2px' }}>AI Counselor</h4>
              {currentLesson ? (
                <p style={{ fontSize: '11px', color: 'var(--orange)', margin: 0, fontWeight: 600 }}>
                  {currentLesson.title}
                </p>
              ) : (
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>Powered by Claude</p>
              )}
            </div>
            <button onClick={() => setTutorOpen(false)} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: '6px', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-muted)' }}>
              <X size={14} />
            </button>
          </div>
          
          {/* Suggested questions */}
          {messages.length === 0 && (
            <div style={{ padding: '16px' }}>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '10px' }}>Suggested questions:</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {SUGGESTED_QUESTIONS.map(q => (
                  <button key={q} onClick={() => { setInputMsg(q); }} style={{ background: 'var(--bg-page)', border: '1px solid var(--border-default)', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', color: 'var(--text-secondary)', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s' }}
                    onMouseEnter={e => { e.target.style.borderColor = 'var(--orange)'; e.target.style.color = '#F97316'; }}
                    onMouseLeave={e => { e.target.style.borderColor = 'var(--border-default)'; e.target.style.color = '#CCCCCC'; }}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {messages.map((msg, i) => (
              <div key={msg.id || i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{ maxWidth: '85%', padding: '10px 14px', borderRadius: '12px', background: msg.role === 'user' ? 'var(--orange)' : 'var(--surface-alt)', border: msg.role === 'user' ? 'none' : '1px solid var(--border)', color: msg.role === 'user' ? '#ffffff' : 'var(--text-body)', fontSize: '13px', fontFamily: 'Inter, sans-serif', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {msg.content || (tutorLoading && i === messages.length - 1 && <span style={{ opacity: 0.5 }}>Thinking...</span>)}
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: '8px' }}>
            <input value={inputMsg} onChange={e => setInputMsg(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendTutorMessage()}
              placeholder="Ask the AI Counselor..."
              style={{ flex: 1, background: 'var(--bg-page)', border: '1px solid var(--border-default)', borderRadius: '8px', color: 'var(--text-primary)', padding: '8px 12px', fontFamily: 'Inter, sans-serif', fontSize: '13px', outline: 'none' }}
              onFocus={e => e.target.style.borderColor = 'var(--orange)'}
              onBlur={e => e.target.style.borderColor = 'var(--border-default)'}
              data-testid="tutor-input"
            />
            <button onClick={sendTutorMessage} disabled={tutorLoading || !inputMsg.trim()}
              style={{ background: '#F97316', border: 'none', borderRadius: '8px', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', opacity: tutorLoading ? 0.6 : 1 }}
              data-testid="tutor-send-btn">
              {tutorLoading ? <Loader size={14} color="#ffffff" className="animate-spin" /> : <Send size={14} color="#ffffff" />}
            </button>
          </div>
        </div>
      </div>

      {/* AI Tutor toggle button */}
      {!tutorOpen && (
        <button onClick={() => setTutorOpen(true)}
          style={{ position: 'fixed', right: '24px', bottom: '24px', background: '#F97316', border: 'none', borderRadius: '50%', width: '52px', height: '52px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 20px rgba(249,115,22,0.4)', zIndex: 30 }}
          data-testid="open-tutor-btn">
          <MessageSquare size={22} color="#ffffff" />
        </button>
      )}
    </div>
  );
}
